import { ClientLogger } from 'bernie-client';
import { ContextInput } from 'bernie-context';
import { QueryObject, SerializedData, timeoutPromise } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { ClientOptions, FetchPolicy } from 'bernie-graphql';
import { getErrorString, Logger } from 'bernie-logger';
import { AnalyticsStore } from 'bernie-plugin-mobx';
import { action, observable, runInAction } from 'mobx';
import { getTrackingInfoForLoggingFromHeaders } from '../common/logging-utils';
import { ActivityDestinationViewSearch, ActivityGroupViewType } from '../common/__generated__/api';
import { initializeSortAndFilterObject } from '../components/srp/sort-filter';
import { updateSearchCriteriaStore } from '../helpers/activity-search-helper';
import { LXStore, RequestWithTraceInfo } from './lx-store';
import { SearchCriteriaStore } from './search-criteria-store';
import { STORE_EVENTS } from './store-events';

type ActivityTextTileInlineFragment = ActivityDestinationViewSearch.ActivityTextTileInlineFragment;

export class ActivitySearchStore extends LXStore {
  @observable public activityGroups: ActivityDestinationViewSearch.ActivityGroups[] = [];
  @observable public destinationViewList: ActivityDestinationViewSearch.ActivityTiles[] = [];
  @observable public listViewActivities: ActivityDestinationViewSearch.ActivityInlineFragment[] = [];
  @observable public loadingActivities?: boolean;
  @observable public isBrowser = true;
  @observable public retainBrowserFocus = false;
  @observable public name?: string;
  @observable public pageInfo: ActivityDestinationViewSearch.PageInfo | null = null;
  @observable public paginationInfo: ActivityDestinationViewSearch.PaginationInfo | null | undefined = null;
  @observable public query: QueryObject = {};
  @observable public regionName?: string;
  @observable public searchError?: boolean;
  @observable public searchSummary: ActivityDestinationViewSearch.SearchSummary | null = null;
  @observable public serverSide?: boolean;
  @observable public universalSortAndFilter?: ActivityDestinationViewSearch.UniversalSortAndFilter | null = null;
  @observable public showMoreButton?: boolean;
  @observable public showMoreLoading?: boolean;
  @observable public uisPayload: any;
  @observable public sortAndFilterObject: SerializedData = {};
  @observable public selectedCategories: SerializedData = [];
  @observable public isGridLayoutListViewEnabled = false;
  public firstLoadActivityCount = 0;

  public constructor(
    bexConfigs: ClientOptions,
    logger: Logger | ClientLogger,
    appConfig: KeyedObject = {},
    requestOrContext?: ContextInput | RequestWithTraceInfo
  ) {
    super(bexConfigs, logger, appConfig, requestOrContext);
    this.logger = logger;

    if (appConfig) {
      this.firstLoadActivityCount = appConfig.firstLoadActivityCount;
    }
  }

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
  }

  public searchSetup(): void {
    this.loadingActivities = true;
    this.showMoreLoading = false;
    this.listViewActivities = [];
    this.searchError = false;
    this.activityGroups = [];
    this.showMoreButton = true;
  }

  public clearResponse(): void {
    this.searchSummary = null;
  }

  public resetActivitySearchStore(): void {
    this.clearResponse();
    this.searchSetup();
    this.searchSummary = null;
    this.uisPayload = undefined;
    this.regionName = undefined;
  }

  private getTraceId(): string {
    return (this.headers && this.headers['trace-id']) || null;
  }

  private getMessageId(): string {
    return (this.headers && this.headers['message-id']) || null;
  }

  // for destination view only
  @action
  public fetchActivitiesSearchDestinationView(
    searchCriteriaStore: SearchCriteriaStore,
    analyticsStore: AnalyticsStore,
    query: QueryObject,
    searchVariables: ActivityDestinationViewSearch.Variables | null,
    pageIdentity: string,
    indexOfGroupToUpdate: number | null,
    overrideAllGroups = true,
    shouldUpdateCriteria = true
  ): Promise<any> {
    if (this.logtracing) {
      this.logger.logEvent(
        STORE_EVENTS.DESTINATION_QUERY_FETCHED,
        JSON.stringify(searchVariables),
        `traceid=${this.getTraceId()} messageid=${this.getMessageId()}`
      );
    }

    return timeoutPromise(
      this.bexApiSource.operations
        .activityDestinationViewSearch(searchVariables, {
          pageId: pageIdentity,
          fetchPolicy: FetchPolicy.NO_CACHE,
        })
        .then((response: KeyedObject) => {
          runInAction('update bex api activities search from response', () => {
            const activitySearch = response.data.activitySearch;
            const showMoreLoading = false;
            this.destinationViewSearchSuccess(
              activitySearch,
              response.extensions.analytics,
              indexOfGroupToUpdate,
              overrideAllGroups,
              showMoreLoading
            );
          });
          searchCriteriaStore.setGroundTransfersBannerCookie();
          if (shouldUpdateCriteria) {
            updateSearchCriteriaStore(searchCriteriaStore, this, query);
          }
          searchCriteriaStore.isQueryStringUpdated = !shouldUpdateCriteria;
        })
        .then(() => {
          if (this.toPublishPageView()) {
            analyticsStore.resetAnalyticsForNewPage();
            analyticsStore.publishClientSidePayload(this.uisPayload);
          }
        })
        .catch((error: Error) => {
          runInAction('recovering from bex api activities search call breakdown', () => {
            this.searchFailed(searchVariables, error);
          });

          throw error;
        }),
      this.serverSide ? this.appConfig.timeout?.serverStoreFetch : this.appConfig.timeout?.clientStoreFetch,
      `store=search fetch=fetchActivitiesSearchDestinationView timeout ${getTrackingInfoForLoggingFromHeaders(
        this.headers
      )}`
    ).catch((error: Error) => {
      runInAction('recovering from bex api activities search call breakdown', () => {
        this.searchFailed(searchVariables, error);
      });

      throw error;
    });
  }

  private searchFailed(searchVariables: ActivityDestinationViewSearch.Variables | null, error: Error): void {
    this.loadingActivities = false;
    this.showMoreLoading = false;
    this.logger.logEvent(STORE_EVENTS.BFF_SEARCH_POOPED, searchVariables, getErrorString(error));
    this.searchError = true;
  }

  private destinationViewSearchSuccess(
    activityDestinationViewSearch: ActivityDestinationViewSearch.ActivitySearch,
    analytics: any,
    indexOfGroupToUpdate: number | null,
    overrideAllGroups: boolean,
    showMoreLoading: boolean
  ): void {
    this.loadingActivities = false;

    if (activityDestinationViewSearch.activityGroups && activityDestinationViewSearch.activityGroups.length > 0) {
      if (overrideAllGroups) {
        this.activityGroups = activityDestinationViewSearch.activityGroups;
      } else {
        const groupWithActivityList = activityDestinationViewSearch.activityGroups.find(
          (group) => group.viewType === ActivityGroupViewType.ACTIVITY_LIST
        );
        if (groupWithActivityList && indexOfGroupToUpdate !== null) {
          this.activityGroups[indexOfGroupToUpdate].activityTiles.push(...groupWithActivityList.activityTiles);
          this.showMoreButton = groupWithActivityList.action !== null;
          const resultsDisplayedTextGroup = activityDestinationViewSearch.activityGroups.find(
            (group) => group.viewType === ActivityGroupViewType.TEXT
          );
          let text = (this.activityGroups[indexOfGroupToUpdate - 1].activityTiles[0] as ActivityTextTileInlineFragment)
            .activityText?.text;
          if (resultsDisplayedTextGroup) {
            if (this.activityGroups[indexOfGroupToUpdate + 1]?.viewType === ActivityGroupViewType.TEXT) {
              this.activityGroups[indexOfGroupToUpdate + 1] = resultsDisplayedTextGroup;
            }
            if (this.activityGroups[indexOfGroupToUpdate - 1]?.viewType === ActivityGroupViewType.TEXT) {
              if (text) {
                text = (resultsDisplayedTextGroup.activityTiles[0] as ActivityTextTileInlineFragment).activityText
                  ?.text;
              }
            }
          }
        }
      }
    }

    this.showMoreLoading = showMoreLoading;
    this.universalSortAndFilter = activityDestinationViewSearch.universalSortAndFilter;
    this.initializeSortFilter();
    // Initialize cateogries which are coming in the URL as filter
    this.initializeCategories(this.query);
    this.uisPayload = analytics;
    this.searchSummary = activityDestinationViewSearch.searchSummary;
    this.pageInfo = activityDestinationViewSearch.searchSummary.pageInfo;
    this.paginationInfo = activityDestinationViewSearch.searchSummary.paginationInfo;
    this.regionName =
      (activityDestinationViewSearch.searchSummary.resolvedRegion &&
        activityDestinationViewSearch.searchSummary.resolvedRegion.regionName) ||
      this.query.location;
    this.searchError = false;
  }

  private toPublishPageView(): boolean {
    return !this.showMoreLoading;
  }

  private initializeSortFilter(): void {
    if (!this.universalSortAndFilter) {
      return;
    }
    this.sortAndFilterObject = initializeSortAndFilterObject(this.universalSortAndFilter);
  }

  private initializeCategories(query: QueryObject): void {
    this.selectedCategories = [];
    const categoriesAndFilter = query && query['filter.categories'] ? query['filter.categories'].split(',') : [];

    const categories = categoriesAndFilter.filter((item) => !this.sortAndFilterObject.filters?.categories[item]);
    const newCategory = categories.filter((item) => !this.selectedCategories.includes(item));
    newCategory?.forEach((key) => {
      this.selectedCategories.push(key);
    });
  }
}
